import { useEffect, useState, useMemo } from "react";
import { Modal, ModalDialog } from "@mui/joy";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import IconButton from "@mui/joy/IconButton";
import Input from "@mui/joy/Input";
import CircularProgress from "@mui/joy/CircularProgress";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import BottomSheet from "../BottomSheet/BottomSheet";
import { bookApi } from "../../api.js";
import { useIsMobile } from "../../hooks/useBreakpoint.js";
import { logEvent } from "../../logger";


// Карточка шаблона (превью + название)
function PresetCard({ preset, applying, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: "pointer",
        borderRadius: "12px", overflow: "hidden",
        border: "2px solid var(--joy-palette-divider)",
        opacity: applying && applying !== preset.id ? 0.5 : 1,
        transition: "all 0.15s",
        "&:hover": { borderColor: "primary.400" },
        "&:active": { transform: "scale(0.97)" },
      }}
    >
      <Box sx={{
        width: "100%", aspectRatio: "3/4",
        bgcolor: preset.cover_bg_color || "#834A90",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {preset.preview_url && (
          <Box component="img" src={preset.preview_url} alt={preset.title}
            draggable={false}
            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        )}
      </Box>
      <Typography level="body-xs" sx={{
        fontSize: "11px", textAlign: "center", p: "5px 4px", fontWeight: 600,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {applying === preset.id ? "Применяем…" : preset.title}
      </Typography>
    </Box>
  );
}

export default function CoverPresetBrowser({ open, onClose, bookId, onApplied }) {
  const isMobile = useIsMobile();
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    setSearch("");
    bookApi.getCoverPresets()
      .then((d) => { if (alive) setPresets(d || []); })
      .catch(() => { if (alive) setPresets([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return presets;
    return presets.filter((p) => (p.title || "").toLowerCase().includes(q));
  }, [presets, search]);

  const apply = async (preset) => {
    if (applying || !bookId) return;
    setApplying(preset.id);
    try {
      logEvent({ source: "ui", event: "editor.cover_preset.selected", book_id: bookId,
        message: "Выбрал шаблон обложки",
        payload: { preset_id: preset.id, preset_title: preset.title || "",
          stage: "selection", persisted: false } });
      const updated = await bookApi.applyCoverPreset(bookId, preset.id);
      onApplied?.(updated);
      onClose?.();
    } catch (e) {
      window.alert("Не удалось применить шаблон обложки. Попробуйте ещё раз.");
    } finally {
      setApplying(null);
    }
  };

  const searchBar = (
    <Input
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      placeholder="Поиск шаблона..."
      startDecorator={<SearchRoundedIcon sx={{ fontSize: 17, color: "text.tertiary" }} />}
      size="sm"
      sx={{ borderRadius: "10px", mb: 1.5 }}
    />
  );

  const grid = loading ? (
    <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
      <CircularProgress size="sm" />
    </Box>
  ) : filtered.length === 0 ? (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, py: 5 }}>
      <ImageRoundedIcon sx={{ fontSize: 36, color: "neutral.300" }} />
      <Typography level="body-sm" color="neutral" textAlign="center">
        Ничего не найдено.
      </Typography>
    </Box>
  ) : (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5 }}>
      {filtered.map((p) => (
        <PresetCard key={p.id} preset={p} applying={applying} onClick={() => apply(p)} />
      ))}
    </Box>
  );

  const title = "Готовые шаблоны обложки";

  // Мобильный — штатная шторка снизу
  if (isMobile) {
    return (
      <BottomSheet open={open} onClose={onClose} title={title}>
        <Box sx={{ p: "4px 16px 20px" }}>
          {searchBar}
          {grid}
        </Box>
      </BottomSheet>
    );
  }

  // Десктоп — модалка
  return (
    <Modal open={open} onClose={onClose} sx={{ zIndex: 10010 }}>
      <ModalDialog sx={{ maxWidth: 640, width: "100%", maxHeight: "85vh", overflowY: "auto", borderRadius: "18px" }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
          <Typography level="title-lg">{title}</Typography>
          <IconButton size="sm" variant="plain" onClick={onClose} sx={{ ml: "auto" }}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>
        {searchBar}
        {grid}
      </ModalDialog>
    </Modal>
  );
}